import type {ReactElement} from 'react';
import {useEffect} from 'react';
import React from 'react';
import {Text, TextSize} from '@Components/text';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import styles from './download-panel-upselling.module.scss';
import {getAssetUrl} from '@Utils/s3.util';
import type {DownloadPanelUpsellingDialogData} from '@Components/download-panel/components/download-panel-upselling/download-panel-upselling.types';
import {DownloadPanelUpsellingTypes} from '@Components/download-panel/components/download-panel-upselling/download-panel-upselling.types';
import {getCompatibleImageFileExtension} from '@Utils/image.util';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {updateFeatureToShowOnLoad, updatePublishDropDownState} from '@Components/poster-editor/poster-editor-reducer';
import {FeatureToShowOnLoad} from '@Components/poster-editor/poster-editor.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {isAndroid, isMobile} from 'react-device-detect';
import {noop} from '@Utils/general.util';
import {SelectionType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {setSelectionType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import {openPosterAddItemsModal} from '@Modals/poster-add-items-modal';
import {AddItemOptions} from '@Panels/poster-add-items-panel/poster-add-items-panel';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {setDownloadUpsellingCookie} from '@Libraries/poster-download-library';

interface DownloadPanelUpsellingProps {
  onClose?(): void;
  isOpenedInModal?: boolean;
}

export function DownloadPanelUpselling({isOpenedInModal = false, onClose = noop}: DownloadPanelUpsellingProps): ReactElement {
  const {posterObject} = useDownloadPanelData();
  const upsellingRecommendationNumber = useAppSelector((state) => state.downloadDialog.upsellingRecommendationNumber) ?? 0;

  const generateTypeForRandomContent = (): DownloadPanelUpsellingDialogData => {
    return DOWNLOAD_PANEL_UPSELLING_DIALOG_DATA[upsellingRecommendationNumber];

    // SOCIAL_MEDIA_SIZE if exist
    // DIGITAL_SIGNAGE_MENU if exist menu
    //QR_CODES_MENU
    // TIMELINE_VIDEO if video
    // TIMELINE_AUDIO if audio
    // MENU_MAKER if menu exist
    // TEAMS IF NOT ALREADY
    // SPELLCHECK IF PAYG
  };

  useEffect(() => {
    trackPosterBuilderGA4Events(GA4EventName.POST_DOWNLOAD_UPSELLING_OPEN, {[GA4EventParamName.TYPE]: generateTypeForRandomContent()?.GAEventId ?? ''});
  }, []);

  const dispatch = useAppDispatch();
  const DOWNLOAD_PANEL_UPSELLING_DIALOG_DATA: DownloadPanelUpsellingDialogData[] = [
    {
      type: DownloadPanelUpsellingTypes.PUBLISH,
      thumbURL: 'download-publish-upselling',
      GAEventId: 'Social_Media',
      text: window.i18next.t('pmwjs_download_publish_upselling_description'),
      buttonProps: {
        text: window.i18next.t('pmwjs_publish_a_post'),
        icon: 'icon-publish-arrow',
        iconPlacement: IconPlacement.RIGHT,
        type: Type.SUCCESS,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.CONTENT_PLANNER,
      GAEventId: 'Content_Planner',
      thumbURL: 'download-content-planner-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_content_planner_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_view_content_planner'),
        icon: 'icon-calendar',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PREMIUM,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.EVENT_GENERAL,
      GAEventId: 'Event_Landing_Page',
      thumbURL: 'download-event-general-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_event_general_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_create_a_free_event'),
        icon: 'icon-event',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PREMIUM,
        size: Size.XSMALL,
      },
      isHidden: true,
    },
    {
      type: DownloadPanelUpsellingTypes.QR_CODE_GENERAL,
      GAEventId: 'QR_Code_General',
      thumbURL: 'download-qr-general-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_qr_code_general_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_share_via_qr_code'),
        icon: 'icon-qr-content',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PRIMARY,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.EMAIL_MAKER,
      GAEventId: 'Email_Maker',
      thumbURL: 'download-email-maker-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_email_maker_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_send_as_email'),
        icon: 'icon-new-email',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PREMIUM2,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.AI_BACKGROUND_REMOVE,
      GAEventId: 'AI_BG_Remove',
      thumbURL: 'download-ai-remove-bg-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_ai_remove_bg_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_remove_background'),
        icon: 'icon-remove-bg',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PREMIUM2,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.AI_IMAGES,
      GAEventId: 'AI_Images',
      thumbURL: 'download-ai-images-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_ai_image_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_create_ai_image'),
        icon: 'icon-ai-image',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PREMIUM2,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.MOBILE_APP,
      GAEventId: 'MobileApp',
      thumbURL: 'download-mobile-app-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_mobile_app_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_download_app'),
        icon: 'icon-download',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PRIMARY,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.BRAND_KIT,
      GAEventId: 'BrandsKit',
      thumbURL: 'download-brand-kits-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_brand_kit_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_create_a_brand_kit'),
        icon: 'icon-brands',
        iconPlacement: IconPlacement.RIGHT,
        type: Type.SUCCESS,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.ANIMATIONS,
      GAEventId: 'Animations',
      thumbURL: 'download-animations-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_animations_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_add_animation'),
        icon: 'icon-animation',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PREMIUM,
        size: Size.XSMALL,
      },
      isHidden: true,
    },
    {
      type: DownloadPanelUpsellingTypes.SOCIAL_MEDIA,
      thumbURL: 'download-social_media-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_social_media_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_publish_a_post'),
        icon: 'icon-publish-arrow',
        iconPlacement: IconPlacement.RIGHT,
        type: Type.SUCCESS,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.SOCIAL_MEDIA_SIZE,
      thumbURL: 'download-ai_captions-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_social_media_size'),
      buttonProps: {
        text: window.i18next.t('pmwjs_publish_a_post'),
        icon: 'icon-publish-arrow',
        iconPlacement: IconPlacement.RIGHT,
        type: Type.PREMIUM2,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.EVENT_CALENDAR,
      thumbURL: 'download-event_calendar-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_event_calendar_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_create_a_free_event'),
        icon: 'icon-events',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PREMIUM2,
        size: Size.XSMALL,
      },
      isHidden: true,
    },
    {
      type: DownloadPanelUpsellingTypes.EVENTS,
      thumbURL: 'download-events-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_events_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_create_a_free_event'),
        icon: 'icon-events',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PREMIUM2,
        size: Size.XSMALL,
      },
      isHidden: true,
    },
    {
      type: DownloadPanelUpsellingTypes.DIGITAL_SIGNAGE_MENU,
      thumbURL: 'download-digital_signage-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_digital_signage_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_publish_to_digital_signage'),
        icon: 'icon-digital-sign',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PRIMARY,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.QR_CODES_MENU,
      thumbURL: 'download-qr_codes_menu-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_qr_codes_menu_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_share_via_qr_code'),
        icon: 'icon-qr-content',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PRIMARY,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.EMAIL_AI_WRITER,
      thumbURL: 'download-email_ai_writer-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_email_ai_writer_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_create_an_email'),
        icon: 'icon-email-stack',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PREMIUM2,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.AI_IMAGE_REMOVE_BACKGROUND,
      thumbURL: 'download-ai_image_remove_background-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_ai_image_remove_background_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_remove_background'),
        icon: 'icon-remove-bg',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PREMIUM,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.AI_VIDEO_REMOVE_BACKGROUND,
      thumbURL: 'download-ai_video_remove_background-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_ai_video_remove_background_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_remove_background'),
        icon: 'icon-remove-bg',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PREMIUM,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.AI_SUBTITLES,
      thumbURL: 'download-ai_subtitles-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_ai_subtitles_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_create_ai_subtitles'),
        icon: 'icon-ai-subtitles',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PREMIUM,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.AI_VOICE,
      thumbURL: 'download-ai_voice-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_ai_voice_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_create_with_ai_voice'),
        icon: 'icon-ai-voice',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PREMIUM,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.RESIZE,
      thumbURL: 'download-resize-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_resize_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_resize'),
        icon: 'icon-resize',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PRIMARY,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.STICKERS,
      thumbURL: 'download-stickers-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_stickers_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_add_stickers'),
        icon: 'icon-sticker',
        iconPlacement: IconPlacement.LEFT,
        type: Type.DANGER,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.TIMELINE_VIDEO,
      thumbURL: 'download-timeline_video-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_timeline_video_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_edit_on_timeline'),
        type: Type.PREMIUM2,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.TIMELINE_AUDIO,
      thumbURL: 'download-timeline_audio-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_timeline_audio_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_edit_on_timeline'),
        type: Type.PREMIUM2,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.BUSINESS_PROFILES,
      thumbURL: 'download-business_profiles-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_business_profiles_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_create_a_business_profile'),
        icon: 'icon-business',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PRIMARY,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.MENU_MAKER,
      thumbURL: 'download-menu_maker-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_menu_maker_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_try_menu_maker'),
        icon: 'icon-layout',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PREMIUM,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.TEAMS,
      thumbURL: 'download-teams-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_teams_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_view_team_plans'),
        type: Type.PREMIUM,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.UPLOAD_FONTS,
      thumbURL: 'download-upload_fonts-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_upload_fonts_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_upload_font'),
        icon: 'icon-upload',
        iconPlacement: IconPlacement.LEFT,
        type: Type.PREMIUM,
        size: Size.XSMALL,
      },
    },
    {
      type: DownloadPanelUpsellingTypes.SPELLCHECK,
      thumbURL: 'download-spellcheck-upselling',
      text: window.i18next.t('pmwjs_download_panel_upselling_spellcheck_text'),
      buttonProps: {
        text: window.i18next.t('pmwjs_use_spellcheck'),
        type: Type.DANGER,
        size: Size.XSMALL,
      },
    },
  ];

  const onClickCTA = (type: DownloadPanelUpsellingTypes): void => {
    const clickedItem = DOWNLOAD_PANEL_UPSELLING_DIALOG_DATA.find((item) => item.type === type);
    if (clickedItem && 'GAEventId' in clickedItem && !!clickedItem.GAEventId) {
      trackPosterBuilderGA4Events(GA4EventName.POST_DOWNLOAD_UPSELLING_CLICK, {[GA4EventParamName.TYPE]: clickedItem?.GAEventId});
    }

    switch (type) {
      case DownloadPanelUpsellingTypes.PUBLISH:
        window.open(window.PMW.util.site_url(`socialmedia/post/design/${posterObject.hashedID}`), '_blank');
        break;
      case DownloadPanelUpsellingTypes.CONTENT_PLANNER:
        window.open(window.PMW.util.site_url('posters/mine#/content-planner'), '_blank');
        break;
      case DownloadPanelUpsellingTypes.EVENT_GENERAL:
        window.open(window.PMW.util.site_url(`event/wizard?st=3&tcph=${posterObject.hashedID}&tcps=1`), '_blank');
        break;
      case DownloadPanelUpsellingTypes.QR_CODE_GENERAL:
        dispatch(updatePublishDropDownState(true));
        setTimeout((): void => {
          dispatch(setSelectionType(SelectionType.QR_CODE));
        }, 10);
        onClose();
        break;
      case DownloadPanelUpsellingTypes.EMAIL_MAKER:
        window.open(window.PMW.util.site_url(`emailmarketing/publish/design/${posterObject.hashedID}`), '_blank');
        break;
      case DownloadPanelUpsellingTypes.AI_BACKGROUND_REMOVE:
      case DownloadPanelUpsellingTypes.AI_IMAGES:
        if (isEditorMobileVariant()) {
          openPosterAddItemsModal({
            panelToShowOnOpen: AddItemOptions.AI_MENU,
          });
        } else {
          dispatch(updateFeatureToShowOnLoad(FeatureToShowOnLoad.AI_REMOVE_BG));
        }
        break;
      case DownloadPanelUpsellingTypes.MOBILE_APP:
        if (isMobile) {
          if (isAndroid) {
            window.open(window.PMW.util.site_url('https://play.google.com/store/apps/details?id=com.app.postermywall12&hl=en'), '_blank');
          } else {
            window.open(window.PMW.util.site_url('https://apps.apple.com/pk/app/postermywall-design-promote/id6469032454'), '_blank');
          }
        } else {
          window.open(window.PMW.util.site_url('m/mobile-app'), '_blank');
        }
        break;
      case DownloadPanelUpsellingTypes.ANIMATIONS:
        // hidden for now
        break;
      case DownloadPanelUpsellingTypes.BRAND_KIT:
        window.open(window.PMW.util.site_url(`posters/mine#/brands`), '_blank');
        break;
      default:
        return;
    }
  };

  const getContent = (): ReactElement => {
    const data = generateTypeForRandomContent();
    return (
      <div className={`radius-8 ${styles.popup} _full-height ${isOpenedInModal ? '' : 'border-s-standard shadow-floating-elevation-3'} _full-width flex-v-row`}>
        <img alt="" className={`_full-width ${styles.imageSrc}`} src={getAssetUrl(`${data.thumbURL}.${getCompatibleImageFileExtension('webp')}`)} />
        {isOpenedInModal ? null : (
          <Icon
            className={styles.closeIcon}
            icon="icon-close"
            size={IconSize.SIZE_ICON_16}
            shape={IconShape.SQUARE}
            type={IconType.DARK_GHOST}
            onClick={(): void => {
              setDownloadUpsellingCookie();
              trackPosterBuilderGA4Events(GA4EventName.POST_DOWNLOAD_UPSELLING_CLOSE, {[GA4EventParamName.TYPE]: data?.GAEventId ?? ''});
              onClose();
            }}
          />
        )}
        <div
          className={`_full-width _full-height ${isOpenedInModal ? '' : 'flex-content-start'} spacing-p-t-4 content-body flex-center flex-v-row flex-items-center ${styles.textContainer}`}
        >
          <Text className={`-text-align-center ${styles.text}`} size={TextSize.MEDIUM} val={data.text} />
          <Button
            customClasses="_fit-width spacing-m-t-3"
            size={data.buttonProps.size}
            type={data.buttonProps.type}
            iconPlacement={data.buttonProps.iconPlacement}
            text={data.buttonProps.text}
            icon={data.buttonProps.icon}
            onClick={(): void => {
              onClickCTA(data.type);
              onClose();
            }}
          />
        </div>
      </div>
    );
  };

  return getContent();
}
